<template>
  <!-- Design-system look: the pages of the current event section (guests, content, event app,
       tickets …) in the second navigation column, headed by the section's illustration, name and
       the event (the organisation outside one). Same entries, gates and links as the section tabs (useSectionPages); from lg up
       it replaces their pill row. Default.vue keys it by section, so it flies in on entering one. -->
  <SectionNavColumn
    :title="$t(`common.${section.label}`)"
    :subtitle="
      usePage().props.event?.name ??
      usePage().props._user?.active_company?.name ??
      null
    "
    :illustration="sidebarIllustration(section.label)"
    :pages="columnPages"
    collapsible
    @collapse="setSectionNavCollapsed(true)"
  >
    <!-- „So funktioniert's“ below Reporting (Arnold, 02.11.2026, UX1). -->
    <template v-if="showPlannerVideo" #after>
      <PlannerVideoNavRow />
    </template>
  </SectionNavColumn>
</template>

<script setup>
import PlannerVideoNavRow from '@/Components/Planner/PlannerVideoNavRow.vue'
import SectionNavColumn from '@/Components/Sidebar/SectionNavColumn.vue'
import { childGroupHeading } from '@/composables/menu'
import { sidebarIllustration } from '@/Libraries/sidebarIllustrations'
import { usePlannerVideoAvailable } from '@/composables/plannerVideo'
import {
  setSectionNavCollapsed,
  useCurrentSectionMenu,
  useSectionPages,
} from '@/composables/sectionTabs'
import { usePage } from '@inertiajs/vue3'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const section = computed(
  () => useCurrentSectionMenu() ?? { label: '', child: [] },
)

const plannerVideoAvailable = usePlannerVideoAvailable()
const showPlannerVideo = computed(
  () => section.value.label === 'planner' && plannerVideoAvailable.value,
)

const pages = useSectionPages()

/**
 * The design system's line icon for each page (menu.js child `label`), so every row carries a
 * motif of its own; most children have no icon in menu.js. Unknown pages get the document icon.
 */
const PAGE_ICONS = {
  event_media: 'image',
  event_texts: 'doc',
  guestlist: 'users',
  guest_groups: 'layers',
  sub_events: 'calendar-plus',
  checkin_history: 'qr-scan',
  checkin_cockpit: 'user-check',
  guestManagersList: 'id-badge',
  device_monitor: 'tablet',
  agenda_builder: 'calendar',
  agenda_speakers: 'mic',
  exhibitors: 'building',
  agenda_locations: 'pin',
  agenda_tracks: 'route',
  setup: 'gear',
  app_config: 'gear',
  app_settings_beta: 'gear',
  app_builder: 'pages',
  app_pages_legacy: 'pages',
  app_navigation: 'menu',
  event_map: 'globe',
  push_notifications: 'bell',
  newsfeed: 'newspaper',
  faqs: 'question',
  surveys: 'list-check',
  galleries: 'film',
  media_library: 'folder',
  media: 'image',
  texts: 'doc',
  directories: 'address-book',
  chat_channels: 'chat',
  quizzes: 'academic',
  logs: 'history',
  getting_started: 'sparkle',
  menu_tickets: 'ticket',
  menu_ticket_design: 'palette',
  bookable_groups: 'cube',
  invoicing: 'file-invoice',
  legal: 'scale',
  payment_provider: 'wallet-card',
  promo_codes: 'tag',
  orders: 'cart',
  email_templates: 'envelope',
  email_senders: 'paper-plane',
  email_senders_custom_smtp: 'server',
  email_statistics: 'chart',
  sent_emails: 'envelope-check',
  opt_in_history: 'check-circle',
  marketing_opt_in_history: 'megaphone',
  sms_template: 'chat',
  sent_sms: 'paper-plane',
  sms_statistics: 'chart',
  sms_sender_identities: 'id-badge',
  sms_pricing: 'currency',
  tableseating_manage: 'seat',
  tableseating_manage_legacy: 'seat',
  tableseating_table: 'table',
  event_information: 'info',
  field_settings: 'columns',
  event_settings: 'gear',
  limits: 'target',
  workflows: 'zap',
  planner_overview: 'chart-pie',
  planner_tasks: 'list-check',
  planner_budget: 'wallet-card',
  planner_vendors: 'building',
  planner_sponsors: 'handshake',
  planner_costs: 'file-invoice',
  planner_documents: 'doc',
  planner_reporting: 'chart',
}

/** The rows, each group of pages headed by its name while more than one group shows (Inhalte). */
const columnPages = computed(() =>
  pages.value.flatMap((page, index) => {
    const heading = childGroupHeading(pages.value, index)
    const row = {
      key: page.label,
      component: page.name,
      label: t(`common.${page.label}`),
      href: page.href,
      icon: PAGE_ICONS[page.label] ?? null,
      active: page.active,
      locked: page.locked,
      badge: page.premium ? t('sidebar.badge.premium') : null,
    }

    return heading === null
      ? [row]
      : [{ key: `heading-${page.group}`, heading: t(heading) }, row]
  }),
)
</script>
